<template>
  <template v-if="show">
    <component :is="'style'">
      main:not(.side-modal-container, .side-modal-container *) { overflow: clip !important; }
    </component>
    <div
      @focusout="closeFocus"
      tabindex="0"
      ref="container"
      :class="[
        'side-modal-container',
        location,
        {
          absolute: false
        }
      ]"
      :style="{ width }"
      :data-cy="dataCy"
    >
      <slot></slot>
    </div>
    <div
      :class="[
        'modal-overlay',
        {
          absolute: false
        }
      ]"
      @click="closeOverlay"
      ref="overlayRef"
    ></div>
  </template>
  <template v-if="!show && !closed">
    <div
      tabindex="0"
      :class="[
        'side-modal-container',
        'hide',
        location,
        {
          absolute: false
        }
      ]"
    >
      <slot></slot>
    </div>
    <div
      :class="[
        'modal-overlay hide',
        {
          absolute
        }
      ]"
    ></div>
    <!-- Test Changes -->
  </template>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";

const props = defineProps<{
  show: boolean;
  closeOnFocusOut?: boolean;
  closeOnOverlayClick?: boolean;
  width?: string;
  dataCy?: string;
  location: "left" | "right";
  absolute?: boolean;
}>();
const emit = defineEmits<{
    (e: 'update:show', ...args: any[]): void
}>();
const closed = ref(true);
let tout = 0;

const container = ref<HTMLDivElement | null>(null);

const closeFocus = (e: FocusEvent) => {
  if (!container.value?.contains(e.relatedTarget as any) && props.closeOnFocusOut) {
    emit("update:show", false);
    closed.value = false;
    clearTimeout(tout);
    tout = window.setTimeout(() => {
      closed.value = true;
      tout = 0;
    }, 500);
  }
};
const overlayRef = ref<HTMLDivElement | null>(null);
const closeOverlay = (e: MouseEvent) => {
  if (overlayRef.value === (e.target as any) && props.closeOnOverlayClick) {
    emit("update:show", false);
    closed.value = false;
    clearTimeout(tout);
    tout = window.setTimeout(() => {
      closed.value = true;
      tout = 0;
    }, 500);
  }
};

// focus on load, so the focus out will apply even if user didn't click on the modal
watch(container, () => {
  container.value?.focus();
});

watch(
  () => props.show,
  () => {
    if (!props.show && !tout) {
      closed.value = false;
      clearTimeout(tout);
      tout = window.setTimeout(() => {
        closed.value = true;
        tout = 0;
      }, 500);
    }
  }
);
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.side-modal-container {
  outline: none;
  position: fixed;
  top: 0;
  right: 0;
  width: 426.667px;
  max-width: 100%;
  background-color: white;
  z-index: 1099;
  height: 100vh;
  display: flex;
  gap: 24px;
  flex-direction: column;
  overflow: auto;
  animation: positionIn 0.5s;
  border-left: 1px solid $gray-200;
  border-right: 1px solid $gray-200;

    &.left {
      animation: positionInLeft 0.5s;
      left: 0;
    }
    &.absolute {
      position: absolute;
      height: 100%;
    }

    &.hide {
      animation: positionIn 0.5s reverse forwards;

      &.left {
        animation: positionInLeft 0.5s reverse forwards;
      }
    }

    @keyframes positionIn {
      0% {
        right: -426px;
      }
      100% {
        right: 0;
      }
    }

    @keyframes positionInLeft {
      0% {
        left: -426px;
      }
      100% {
        left: 0;
      }
    }

    @media screen and (max-width: 576px) {
      width: 100%;
      > .item:first-child {
        margin-top: 0px !important;
      }
    }

    .actions {
      flex: 1;
      display: flex;
      align-items: flex-end;

      > .action-items {
        display: flex;
        justify-content: space-between;
        width: 100%;
      }
    }
  }
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100%;
  opacity: 0;
  animation: fadeOut 0.5s reverse forwards;
  background-color: #0e0a0a3b;
  z-index: 1098;

  &.hide {
    animation: fadeOut 0.5s forwards linear;
  }

  &.absolute {
    position: absolute;
    height: 100%;
  }
}
@keyframes fadeOut {
  0% {
    opacity: 1;
  }
  99% {
    opacity: 0;
  }
}
</style>
